<template>
  <v-navigation-drawer
    v-model="isDrawerOpen"
    app
    right
    temporary
    hide-overlay
    class="pa-2 shift-report-center-alignement"
    :width="isMobile ? '100%' : '375px'"
    style="background-color: var(--color-element-layer1); height: 100%; z-index: 10"
  >
    <div class="multi-row pl-2 mb-4 justify-space-between ">
      <!-- Title Row -->
      <v-row no-gutters class="mb-4">
        <v-col>
          <div class="d-flex align-center justify-space-between">
            <p class="wx-typo-h2">{{ $t("shiftReport.form.title") }}</p>
            <wx-btn-icon text @click="closeMenu">
              <v-icon>mdi-close</v-icon>
            </wx-btn-icon>
          </div>
        </v-col>
      </v-row>
      <div>
        <v-row no-gutters class="mb-6 shift-dynamic-text">
          <v-col>
            <p class="wx-typo-norm">{{ shiftName }}</p>
          </v-col>
        </v-row>

        <!-- Current Day and Navigation -->
        <v-row no-gutters class="mb-2">
          <v-col class="d-flex align-center">
            <p class="wx-typo-norm mb-0">{{ selectedShiftStartDay }}</p>
          </v-col>
          <v-col class="d-flex align-center nav-component" cols="auto">
            <wx-btn-icon
              @click="selectPreviousShiftReport"
              :title="$t('common.previous')"
              :disabled="isPreviousButtonDisabled"
              style="height: 30px"
              text
            >
              <v-icon>mdi-chevron-left</v-icon>
            </wx-btn-icon>
            <wx-btn-standard
              @click="goToPresent()"
              :title="$t('timeNavigator.presentBtn')"
              :disabled="isPresentButtonDisabled"
              class="mx-1"
              small
              text
            >
              {{ $t("timeNavigator.presentBtn") }}
            </wx-btn-standard>
            <wx-btn-icon
              @click="selectNextShiftReport"
              :title="$t('common.next')"
              :disabled="isNextButtonDisabled"
              text
            >
              <v-icon>mdi-chevron-right</v-icon>
            </wx-btn-icon>
          </v-col>
        </v-row>
        <shift-report :report="currentShiftReport" />
      </div>
    </div>
  </v-navigation-drawer>
</template>

<script>
import WxBtnIcon from "@/components/ui/WxBtnIcon.vue";
import WxBtnStandard from "@/components/ui/WxBtnStandard.vue";
import ShiftReport from "@/components/dashboard/shiftReports/ShiftReport.vue";
import { mapActions, mapGetters, mapMutations } from "vuex";
import { getDateFromMillis } from "@/store/TimeUtils";
import { DateTime } from "luxon";

export default {
  name: "ShiftReports",
  components: { WxBtnStandard, WxBtnIcon, ShiftReport },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      sentiment: null,
      countDownInSeconds: 0,
      // this will cycle through the reports
      reportIndex: -1,
    };
  },
  computed: {
    ...mapGetters("shiftReports", ["shiftReports", "isShiftReportsDialogOpen"]),
    ...mapGetters("dashboard", ["activeProductionUnit", "workShiftCoverage"]),
    ...mapGetters("navigation", ["activeFactory"]),
    ...mapGetters("alerts", ["alerts", "activeAlertInstance"]),
    isShiftSubmissionDisabled() {
      return true;
    },
    isUnitSelectionDisabled() {
      return true;
    },
    isDrawerOpen: {
      get() {
        return this.isShiftReportsDialogOpen;
      },
      set(value) {
        this.setIsShiftReportsDialogOpen(value);
      },
    },
    isMobile() {
      return this.$vuetify.breakpoint.mdAndDown;
    },
    isPreviousButtonDisabled() {
      if (this.shiftReports && this.shiftReports.length > 0) {
        return this.reportIndex === 0;
      }
      return true;
    },
    isPresentButtonDisabled() {
      if (this.shiftReports && this.shiftReports.length > 0) {
        return this.reportIndex === this.shiftReports.length - 1;
      }
      return true;
    },
    isNextButtonDisabled() {
      if (this.shiftReports && this.shiftReports.length > 0) {
        return this.reportIndex === this.shiftReports.length - 1;
      }
      return true;
    },
    shiftName() {
      if (this.currentShiftReport) {
        return this.currentShiftReport.workShiftName;
      }
      return "";
    },
    selectedShiftStartDay() {
      if (this.currentShiftReport) {
        return getDateFromMillis(
          this.currentShiftReport.associatedWorkShiftCoverage.start_millis_utc,
          this.activeFactory.timezone,
        );
      }
      return "";
    },
    currentShiftReport() {
      if (this.reportIndex === -1 || !this.shiftReports || this.shiftReports.length === 0) {
        return null;
      }
      return this.shiftReports[this.reportIndex];
    },
  },
  watch: {
    shiftReports() {
      if (!this.shiftReports || this.shiftReports.length === 0) {
        this.reportIndex = -1;
      } else {
        this.reportIndex = this.shiftReports.length - 1;
      }
    },
    isShiftReportsDialogOpen() {
      if (this.shiftReports && this.shiftReports.length > 0) {
        this.reportIndex = this.shiftReports.length - 1;
      }
    },
    activeProductionUnit(newValue, oldValue) {
      if (newValue && newValue?.id !== oldValue?.id) {
        this.tryInitializeWorkShiftReports();
      }
    },
    workShiftCoverage(newValue, oldValue) {
      if (newValue && newValue.length !== oldValue?.length) {
        this.tryInitializeWorkShiftReports();
      }
    },
    users(newValue, oldValue) {
      if (newValue && newValue.length !== oldValue?.length) {
        this.tryInitializeWorkShiftReports();
      }
    },
    reportIndex() {
      if (this.reportIndex !== -1) {
        this.setActiveAlertInstance(null);
      }
    },
  },
  mounted() {
    this.tryInitializeWorkShiftReports();
  },
  methods: {
    ...mapActions("shiftReports", ["clearWorkShiftReports", "fetchShiftReports"]),
    ...mapMutations("shiftReports", ["setIsShiftReportsDialogOpen"]),
    ...mapMutations("alerts", ["setActiveAlertInstance"]),

    closeMenu() {
      this.setIsShiftReportsDialogOpen(false);
      this.setActiveAlertInstance(null);
    },
    tryInitializeWorkShiftReports() {
      if (!this.activeProductionUnit) {
        return;
      }
      if (!this.workShiftCoverage || this.workShiftCoverage.length === 0) {
        return;
      }
      const oneWeekMs = 7 * 24 * 60 * 60 * 1000;
      const nowUtcMs = DateTime.now().toMillis();
      this.clearWorkShiftReports();
      this.fetchShiftReports({
        queryStartTime: nowUtcMs - oneWeekMs,
        queryEndTime: nowUtcMs + oneWeekMs,
      });
    },
    selectNextShiftReport() {
      if (this.shiftReports) {
        this.reportIndex = Math.min(this.reportIndex + 1, this.shiftReports.length - 1);
      }
    },
    selectPreviousShiftReport() {
      if (this.shiftReports) {
        this.reportIndex = Math.max(this.reportIndex - 1, 0);
      }
    },
    goToPresent() {
      if (this.shiftReports) {
        this.reportIndex = this.shiftReports.length - 1;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.multi-row {
  flex-direction: column;
}

.shift-report-center-alignement {
  align-items: center;
  flex-direction: column;
  display: flex;
}

.shift-reports-container {
  width: 100%;
}
.shift-dynamic-text {
  height: 25px;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
